import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { getUserFeedItems, getNextFeedBatch, getPostOnce } from '../../services/firebase/feed.service';
import { getDbPathForPost, postTypeNeedsOwner } from '../../services/firebase/feed-utils';
// Post interface is imported but used in comments for type reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Post, FeedPostType, DEFAULT_FEED_FILTER } from '../../types/feed';
import { RootState } from '../index';
import { selectShadowbannedUidsSet } from './shadowbanSlice';
import { selectNonPublicUidsSet } from './privacySlice';
import logger from '../../utils/logger';

// Define types for our slice state
interface FeedState {
  feedItemsList: string[];  // Ordered list of feed item IDs
  feedItemsData: Record<string, any>;  // Feed item metadata
  posts: Record<string, any>;  // The actual content of posts
  isLoading: boolean;
  isLoadingMore: boolean;  // Separate flag for pagination (Load More)
  hasMoreItems: boolean;   // Whether there are more items to load
  error: string | null;
  fetchedBatchesCount: number;
}

// Initial state
const initialState: FeedState = {
  feedItemsList: [],
  feedItemsData: {},
  posts: {},
  isLoading: false,
  isLoadingMore: false,
  hasMoreItems: true,
  error: null,
  fetchedBatchesCount: 1
};

// Module-level flag to track if a fetch is in progress
// This prevents race conditions from multiple simultaneous fetch calls
let isFetchInProgress = false;
let fetchInProgressForUid: string | null = null;
let fetchStartTime: number = 0;

// Safety timeout to auto-reset flag if it gets stuck (20 seconds)
const FETCH_FLAG_TIMEOUT = 20000;

// Async thunks
export const fetchFeedItems = createAsyncThunk(
  'feed/fetchFeedItems',
  async (uid: string, { dispatch, getState }) => {
    // Check if fetch is already in progress for the SAME user
    // If different user, allow the fetch (user switched)
    const now = Date.now();
    const flagIsStale = (now - fetchStartTime) > FETCH_FLAG_TIMEOUT;

    if (isFetchInProgress && fetchInProgressForUid === uid && !flagIsStale) {
      logger.info('[feedSlice] Feed fetch already in progress for same user, skipping duplicate');
      const currentState = getState() as { feed: FeedState };
      // Return current data to avoid hanging promise
      return {
        feedItemsList: currentState.feed.feedItemsList,
        feedItemsData: currentState.feed.feedItemsData
      };
    }

    // If flag is stale (stuck for > 20s) or different user, reset and proceed
    if (flagIsStale && isFetchInProgress) {
      logger.warn('[feedSlice] Fetch flag was stale, resetting');
    }

    isFetchInProgress = true;
    fetchInProgressForUid = uid;
    fetchStartTime = now;

    return new Promise<{ feedItemsList: string[], feedItemsData: any }>((resolve, reject) => {
      try {
        // Calculate wanted items count based on batch size
        const state = getState() as { feed: FeedState };
        const batchSize = 10;
        const wantedItemsCount = state.feed.fetchedBatchesCount * batchSize;
        
        // Unsubscribe function - intentionally unused (subscription kept active for real-time updates)
        // Prefixed with underscore to indicate it's intentionally not called
        let _unsubscribe: () => void;

        // Callback to process feed items
        const handleFeedItems = (feedItemsList: string[], feedItemsData: any) => {
          // Reset flags since fetch completed
          isFetchInProgress = false;
          fetchInProgressForUid = null;

          // Get current posts
          const { posts } = (getState() as { feed: FeedState }).feed;

          // Find which items we need to fetch full data for
          const unfetchedItems = feedItemsList.filter(key => !posts[key]);

          // Fetch data for each unfetched post
          if (unfetchedItems.length > 0) {
            dispatch(fetchPostsData({ unfetchedItems, feedItemsData }));
          }

          // Keep the subscription active for real-time updates (don't unsubscribe)
          // The subscription will be cleaned up by the subscription manager on logout

          // Resolve the promise with the feed data
          resolve({ feedItemsList, feedItemsData });
        };

        // Start the feed items fetch - note parameter order: uid, count, callback
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        _unsubscribe = getUserFeedItems(uid, wantedItemsCount, handleFeedItems);
      } catch (error) {
        // Reset flags on error
        isFetchInProgress = false;
        fetchInProgressForUid = null;
        logger.error('Error in fetchFeedItems:', error);
        reject(error);
      }
    });
  }
);

export const fetchNextBatch = createAsyncThunk(
  'feed/fetchNextBatch',
  async (uid: string, { dispatch, getState }) => {
    return new Promise<{ feedItemsList: string[], feedItemsData: any, batchNumber: number }>((resolve, reject) => {
      try {
        // Get the current state to determine what we've already fetched
        const state = getState() as { feed: FeedState };
        const currentBatchNumber = state.feed.fetchedBatchesCount + 1;
        
        // Get the date of the last feed item in the current list
        const getLastFeedItemDate = () => {
          const { feedItemsList, feedItemsData } = state.feed;

          // Check if we have any items at all
          if (feedItemsList.length === 0) {
            logger.warn('No feed items to load more from');
            return null;
          }

          // Get the last item in the current list (not theoretical batch calculation)
          const lastItemId = feedItemsList[feedItemsList.length - 1];
          const lastFeedItem = feedItemsData[lastItemId];

          if (!lastFeedItem?.dateCreated) {
            logger.warn('Last feed item missing dateCreated', { lastItemId, lastFeedItem });
            return null;
          }

          logger.log('Loading more from date:', lastFeedItem.dateCreated, 'current count:', feedItemsList.length);
          return lastFeedItem.dateCreated;
        };
        
        const lastFeedItemDate = getLastFeedItemDate();
        
        // If we don't have a valid last date, there are no more items
        if (!lastFeedItemDate) {
          resolve({ feedItemsList: [], feedItemsData: {}, batchNumber: currentBatchNumber });
          return;
        }
        
        // Callback to process feed items
        const handleFeedItems = (feedItemsList: string[], feedItemsData: any) => {

          // Get current posts
          const { posts } = (getState() as { feed: FeedState }).feed;

          // Find which items we need to fetch full data for
          const unfetchedItems = feedItemsList.filter(key => !posts[key]);

          // Fetch data for each unfetched post
          if (unfetchedItems.length > 0) {
            dispatch(fetchPostsData({ unfetchedItems, feedItemsData }));
          }

          // Resolve the promise with the feed data and batch number
          resolve({ feedItemsList, feedItemsData, batchNumber: currentBatchNumber });
        };

        // Start the one-shot batch fetch (no realtime listener - pagination
        // batches don't need updates, and per-page listeners leaked until logout)
        getNextFeedBatch(uid, lastFeedItemDate, handleFeedItems);
      } catch (error) {
        logger.error('Error in fetchNextBatch:', error);
        reject(error);
      }
    });
  }
);

export const fetchPostsData = createAsyncThunk(
  'feed/fetchPostsData',
  async ({ unfetchedItems, feedItemsData }: {
    unfetchedItems: string[],
    feedItemsData: Record<string, any>
  }) => {
    const posts: Record<string, any> = {};

    const fetchPromises = unfetchedItems.map(async (key) => {
      const feedItem = feedItemsData[key];

      if (!feedItem) {
        posts[key] = null;
        logger.warn(`Feed item ${key} has no data`);
        return;
      }

      const dbPath = getDbPathForPost(feedItem, key);
      if (!dbPath) {
        posts[key] = null;
        logger.warn(
          'feedSlice.fetchPostsData - Unknown feed item type.',
          `postType: ${feedItem.postType}, type: ${feedItem.type}`
        );
        return;
      }

      const postData = await getPostOnce(dbPath);
      const type = feedItem.postType || feedItem.type || '';
      // Inject owner for post types that don't store it in the post data
      posts[key] = (feedItem.owner && postTypeNeedsOwner(type))
        ? { ...postData, owner: feedItem.owner }
        : postData;
    });

    await Promise.all(fetchPromises);
    return posts;
  }
);

// Create the slice
const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    resetFeed: () => {
      // Also reset the module-level flags when resetting feed
      isFetchInProgress = false;
      fetchInProgressForUid = null;
      fetchStartTime = 0;
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearLoading: (state) => {
      // Also reset the module-level flags when clearing loading
      isFetchInProgress = false;
      fetchInProgressForUid = null;
      fetchStartTime = 0;
      state.isLoading = false;
      state.isLoadingMore = false;
      state.error = null;
    },
    // Add action to handle posts data - matching original app
    gotPostsData: (state, action) => {
      state.posts = { ...state.posts, ...action.payload };
    },
    updatePostLikeStatus: (state, action) => {
      const { postId, userId, liked } = action.payload;
      
      // Update in feedItemsData
      if (state.feedItemsData[postId]) {
        // Initialize likes if needed
        if (!state.feedItemsData[postId].likes) {
          state.feedItemsData[postId].likes = {};
        }
        
        // Update like status
        if (liked) {
          state.feedItemsData[postId].likes[userId] = true;
          // Update like count
          const currentCount = state.feedItemsData[postId].likesCount || (state.feedItemsData[postId] as any).likeCount || 0;
          state.feedItemsData[postId].likesCount = currentCount + 1;
        } else {
          // Remove like
          delete state.feedItemsData[postId].likes[userId];
          // Update like count
          const currentCount = state.feedItemsData[postId].likesCount || (state.feedItemsData[postId] as any).likeCount || 0;
          state.feedItemsData[postId].likesCount = Math.max(0, currentCount - 1);
        }
        
        // Update hasLiked field based on current user's like status
        state.feedItemsData[postId].hasLiked = !!state.feedItemsData[postId].likes[userId];
      }
      
      // Update in posts
      if (state.posts[postId]) {
        // Initialize likes if needed
        if (!state.posts[postId].likes) {
          state.posts[postId].likes = {};
        }
        
        // Update like status
        if (liked) {
          state.posts[postId].likes[userId] = true;
          // Update like count
          const currentCount = state.posts[postId].likesCount || (state.posts[postId] as any).likeCount || 0;
          state.posts[postId].likesCount = currentCount + 1;
        } else {
          // Remove like
          delete state.posts[postId].likes[userId];
          // Update like count
          const currentCount = state.posts[postId].likesCount || (state.posts[postId] as any).likeCount || 0;
          state.posts[postId].likesCount = Math.max(0, currentCount - 1);
        }
        
        // Update hasLiked field based on current user's like status
        state.posts[postId].hasLiked = !!state.posts[postId].likes[userId];
      }
    },
    // New action to handle real-time Firebase data updates
    handleFirebaseDataUpdate: (state, action) => {
      const { postId, firebaseData, currentUserId } = action.payload;

      // Process real-time Firebase updates for both feedItemsData and posts
      if (state.feedItemsData[postId] && firebaseData) {
        // Update the entire post data from Firebase
        state.feedItemsData[postId] = {
          ...state.feedItemsData[postId],
          ...firebaseData,
          // Recalculate hasLiked from Firebase likes data
          hasLiked: !!(firebaseData.likes && currentUserId && firebaseData.likes[currentUserId])
        };
      }

      if (state.posts[postId] && firebaseData) {
        // Update the entire post data from Firebase
        state.posts[postId] = {
          ...state.posts[postId],
          ...firebaseData,
          // Recalculate hasLiked from Firebase likes data
          hasLiked: !!(firebaseData.likes && currentUserId && firebaseData.likes[currentUserId])
        };
      }
    },
    // Update comment count for a post - keeps feed in sync when navigating back
    updatePostCommentCount: (state, action) => {
      const { postId, delta } = action.payload; // delta: +1 for add, -1 for remove

      // Update in feedItemsData
      if (state.feedItemsData[postId]) {
        const currentCount = state.feedItemsData[postId].commentsCount || state.feedItemsData[postId].commentCount || 0;
        state.feedItemsData[postId].commentsCount = Math.max(0, currentCount + delta);
      }

      // Update in posts
      if (state.posts[postId]) {
        const currentCount = state.posts[postId].commentsCount || state.posts[postId].commentCount || 0;
        state.posts[postId].commentsCount = Math.max(0, currentCount + delta);
      }
    },
  },
  extraReducers: (builder) => {
    // Handle fetchFeedItems
    builder
      .addCase(fetchFeedItems.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFeedItems.fulfilled, (state, action) => {
        state.isLoading = false;
        state.feedItemsList = action.payload.feedItemsList;
        state.feedItemsData = action.payload.feedItemsData;
        state.hasMoreItems = true; // Reset on fresh load
      })
      .addCase(fetchFeedItems.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Failed to fetch feed items';
      })

    // Handle fetchPostsData
      .addCase(fetchPostsData.fulfilled, (state, action) => {
        // Update posts with fetched data
        state.posts = { ...state.posts, ...action.payload };
      })

    // Handle fetchNextBatch — uses isLoadingMore (not isLoading) to avoid top-level spinner
      .addCase(fetchNextBatch.pending, (state) => {
        state.isLoadingMore = true;
        state.error = null;
      })
      .addCase(fetchNextBatch.fulfilled, (state, action) => {
        state.isLoadingMore = false;

        const { feedItemsList, feedItemsData } = state;
        const { feedItemsList: newFeedItemsList, feedItemsData: newFeedItemsData, batchNumber } = action.payload;

        // If no new items returned, there are no more to load
        if (newFeedItemsList.length === 0) {
          state.hasMoreItems = false;
        }

        // Merge the lists and data
        state.feedItemsList = [...feedItemsList, ...newFeedItemsList.filter(id => !feedItemsList.includes(id))];
        state.feedItemsData = { ...feedItemsData, ...newFeedItemsData };
        state.fetchedBatchesCount = batchNumber;
      })
      .addCase(fetchNextBatch.rejected, (state, action) => {
        state.isLoadingMore = false;
        state.error = action.error.message || 'Failed to load more';
      });
  },
});

// Export actions and reducer
export const {
  resetFeed,
  clearError,
  clearLoading,
  updatePostLikeStatus,
  handleFirebaseDataUpdate,
  updatePostCommentCount
} = feedSlice.actions;
export default feedSlice.reducer;

// Selectors
export const selectFeedItems = (state: { feed: FeedState }) => state.feed.feedItemsList;
export const selectFeedItemsData = (state: { feed: FeedState }) => state.feed.feedItemsData;
export const selectPosts = (state: { feed: FeedState }) => state.feed.posts;
export const selectIsLoading = (state: { feed: FeedState }) => state.feed.isLoading;
export const selectIsLoadingMore = (state: { feed: FeedState }) => state.feed.isLoadingMore;
export const selectHasMoreItems = (state: { feed: FeedState }) => state.feed.hasMoreItems;
export const selectError = (state: { feed: FeedState }) => state.feed.error;
export const selectFetchedBatchesCount = (state: { feed: FeedState }) => state.feed.fetchedBatchesCount;

// Custom selector to get combined feed data ready for display
// This is now a memoized selector that will only recalculate when inputs change

// First, create simple selectors for each piece of state we need
const selectFeedItemsList = (state: RootState) => state.feed.feedItemsList;
const selectFeedItemsDataObj = (state: RootState) => state.feed.feedItemsData;
const selectPostsObj = (state: RootState) => state.feed.posts;
const selectViewedProfiles = (state: RootState) => state.user?.viewedProfiles || {};
const selectCurrentUserId = (state: RootState) => state.auth?.user?.uid || '';
const selectIsCurrentUserAdmin = (state: RootState) => state.user?.currentProfile?.isAdmin || false;
// Feed filter selector - returns enabled post types (defaults if filter not loaded yet)
const selectEnabledPostTypes = (state: RootState): FeedPostType[] =>
  state.feedFilter?.enabledTypes || DEFAULT_FEED_FILTER;

// Memoized Set for O(1) post type lookups - prevents recreating Set on every selector run
const selectEnabledPostTypesSet = createSelector(
  [selectEnabledPostTypes],
  (types): Set<string> => new Set(types)
);

// Then create a memoized selector that combines them
// Uses Set for O(1) shadowban and privacy lookups instead of O(n) array includes
export const selectFeedItemsWithData = createSelector(
  [selectFeedItemsList, selectFeedItemsDataObj, selectPostsObj, selectViewedProfiles, selectCurrentUserId, selectShadowbannedUidsSet, selectIsCurrentUserAdmin, selectEnabledPostTypesSet, selectNonPublicUidsSet],
  (feedItemsList, feedItemsData, posts, viewedProfiles, currentUserId, shadowbannedUidsSet, isCurrentUserAdmin, enabledTypesSet, nonPublicUidsSet) => {

    // This is the EXACT pattern used in the original app's FeedFriendsScreen component
    // First map feed item keys to post data, then filter out any missing posts
    const postsList = feedItemsList
      .map(itemId => {
        // Get the feed item data and post data
        const feedItem = feedItemsData[itemId];
        const post = posts[itemId];

        // Skip items with missing data
        if (!feedItem || !post) {
          return null;
        }

        // SHADOWBAN FILTERING:
        // - Non-admin users: Hide posts from shadowbanned users (unless it's their own post)
        // - Admin users: Show all posts (with indicator added later)
        // - Users always see their own posts regardless of shadowban status
        const postOwnerId = feedItem.owner || post.owner;
        const isShadowbanned = shadowbannedUidsSet.has(postOwnerId);
        const isOwnPost = postOwnerId === currentUserId;

        if (isShadowbanned && !isCurrentUserAdmin && !isOwnPost) {
          // Non-admin viewing a shadowbanned user's post - hide it
          return null;
        }

        // PRIVACY FILTERING:
        // Hide posts from users with non-public profiles (unless own post or admin)
        const isNonPublic = nonPublicUidsSet.has(postOwnerId);
        if (isNonPublic && !isCurrentUserAdmin && !isOwnPost) {
          return null;
        }

        // POST TYPE FILTERING:
        // Filter out posts that don't match the enabled post types
        const postType = feedItem.postType || post.postType || post.type;
        if (postType && !enabledTypesSet.has(postType as FeedPostType)) {
          return null;
        }

        // Process post data

        // Get profile from viewed profiles if available
        const ownerProfile = feedItem.owner && viewedProfiles[feedItem.owner];
        
        // If owner is in viewed profiles, use that data first
        const authorDisplayName = 
          (ownerProfile?.displayName || 
           ownerProfile?.username ||
           `${ownerProfile?.firstName || ''} ${ownerProfile?.lastName || ''}`.trim()) ||
          feedItem.author?.displayName || 
          post.authorDisplayName ||
          post.displayName || 
          'Unknown User';
          
        const authorUsername = 
          ownerProfile?.username ||
          feedItem.author?.username || 
          post.username || 
          '';
          
        const authorPhotoURL = 
          ownerProfile?.profilePhotoURL || ownerProfile?.photoURL ||
          feedItem.author?.profilePhotoURL || feedItem.author?.photoURL || 
          post.profilePhotoURL || post.photoURL || 
          null;
          
        const isInstructor = ownerProfile?.isInstructor || false;
        
        // Combine feedItem data with post data
        // Handle both property naming formats (likeCount vs likesCount)
        // CRITICAL FIX: Be extremely careful about how we handle the likes object
        // It could be null, undefined, an empty object, a populated object, or even a boolean in some cases
        
        // Get the likes object from either feedItem or post, ensuring it's actually an object
        let likes: Record<string, any> = {};
        if (feedItem.likes && typeof feedItem.likes === 'object') {
          likes = feedItem.likes as Record<string, any>;
        } else if (post.likes && typeof post.likes === 'object') {
          likes = post.likes as Record<string, any>;
        }
        
        // Combine with likedBy formats as well
        let likedBy: Record<string, any> = {};
        if (feedItem.likedBy && typeof feedItem.likedBy === 'object') {
          likedBy = feedItem.likedBy as Record<string, any>;
        } else if (post.likedBy && typeof post.likedBy === 'object') {
          likedBy = post.likedBy as Record<string, any>;
        }
        
        // Count likes from the objects - this is the source of truth
        // The likesCount field is NOT maintained by Cloud Functions for most post types,
        // so we should always calculate from the likes object when available
        const likesFromObject = Object.keys(likes).length;
        const likesFromLikedBy = Object.keys(likedBy).length;

        // PRIORITY: likes object > likedBy object > explicit count field
        // This ensures we always show the accurate count from the source of truth
        const explicitLikeCount = feedItem.likeCount || feedItem.likesCount || post.likeCount || post.likesCount;
        const likeCount = likesFromObject > 0 ? likesFromObject :
                          (likesFromLikedBy > 0 ? likesFromLikedBy :
                          (typeof explicitLikeCount === 'number' ? explicitLikeCount : 0));
        
        const comments = feedItem.comments || post.comments || {};
        const commentCount = feedItem.commentCount || feedItem.commentsCount || post.commentCount || post.commentsCount || 
                            (comments && typeof comments === 'object' ? Object.keys(comments).length : 0);
                            
        // Determine if the current user has liked this post - check all formats
        // This is a critical calculation since it affects the UI display
        let hasLiked = false;
        
        if (currentUserId) {
          // Check both likes and likedBy objects
          if (likesFromObject > 0 && likes[currentUserId]) {
            hasLiked = true;
          } else if (likesFromObject > 0 && likes[`${currentUserId}`]) {
            hasLiked = true;
          } else if (likesFromLikedBy > 0 && likedBy[currentUserId]) {
            hasLiked = true;
          } else if (likesFromLikedBy > 0 && likedBy[`${currentUserId}`]) {
            hasLiked = true;
          }
        }
        
        // Convert photos from Record<string, Photo> to Array<Photo> for feed display
        // Original app stores photos as { "0": {...}, "1": {...} } but feed expects array
        let photosArray: Array<{ url: string; aspectRatio?: number }> | undefined;
        if (post.photos) {
          if (Array.isArray(post.photos)) {
            // Already an array
            photosArray = post.photos;
          } else if (typeof post.photos === 'object') {
            // Convert Record to Array, sorting by numeric keys
            photosArray = Object.keys(post.photos)
              .sort((a, b) => parseInt(a) - parseInt(b))
              .map(key => (post.photos as any)[key])
              .filter(photo => photo && photo.url);
          }
        }

        return {
          ...post,
          id: itemId,
          key: post.key || itemId,
          postType: feedItem.postType,
          owner: feedItem.owner,
          dateCreated: feedItem.dateCreated,
          photos: photosArray, // Converted to array format
          likes: likes,
          likeCount: likeCount,
          likesCount: likeCount, // Add for backward compatibility
          comments: comments,
          commentCount: commentCount,
          commentsCount: commentCount, // Add for backward compatibility
          hasLiked: hasLiked,
          itemRef: feedItem.itemRef,
          // Shadowban indicator for admin UI
          isShadowbanned: isShadowbanned,
          author: {
            uid: feedItem.owner || post.owner || '',
            displayName: authorDisplayName,
            username: authorUsername,
            photoURL: authorPhotoURL,
            isInstructor: isInstructor
          }
        };
      })
      .filter(Boolean);

    return postsList as Post[];
  }
);